import React from 'react';
import { bool, func, object, string } from 'prop-types';
import classNames from 'classnames';
import { FormattedMessage } from '../../util/reactIntl';
import { ensureOwnListing } from '../../util/data';
import { LISTING_STATE_DRAFT } from '../../util/types';
import { EMPTY_CULTURAL_EXPOSURE_ROW, EMPTY_ROLE_ROW } from '../../marketplace-custom-config';
import { ListingLink } from '../../components';
import { EditListingDescriptionForm } from '../../forms';

import css from './EditListingDescriptionPanel.module.css';

const asRows = (value, emptyRow, maxRows) => {
  const rows = Array.isArray(value) ? value.slice(0, maxRows) : [];
  return rows.length > 0 ? rows : [{ ...emptyRow }];
};

const asRowsPadded = (value, emptyRow, targetRows) => {
  const rows = Array.isArray(value) ? value.slice(0, targetRows) : [];
  const result = [...rows];
  while (result.length < targetRows) result.push({ ...emptyRow });
  return result;
};

const trimString = value => (typeof value === 'string' ? value.trim() : '');

const normalizeRoleRows = rows =>
  (Array.isArray(rows) ? rows : [])
    .slice(0, 3)
    .map(row => ({
      jobTitle: trimString(row?.jobTitle),
      company: trimString(row?.company),
      dates: trimString(row?.dates),
    }))
    .filter(row => row.jobTitle || row.company || row.dates);

const normalizeCulturalExposureRows = rows =>
  (Array.isArray(rows) ? rows : [])
    .slice(0, 10)
    .map(row => ({
      countryRegion: trimString(row?.countryRegion),
      dates: trimString(row?.dates),
    }))
    .filter(row => row.countryRegion || row.dates);

const EditListingDescriptionPanel = props => {
  const {
    className,
    rootClassName,
    listing,
    disabled,
    ready,
    onSubmit,
    onChange,
    submitButtonText,
    panelUpdated,
    updateInProgress,
    errors,
  } = props;

  const classes = classNames(rootClassName || css.root, className);
  const currentListing = ensureOwnListing(listing);
  const {
    description,
    publicData : {
      introductionVideo,
      coachingExperience,
      previousClients,
      coachingFormat,
      coachingExperienceByCareerPhase,
      previousCoacheeTitles,
      coachCareerExperienceRoles: rawCoachCareerExperienceRoles,
      coachCareerExperienceCulturalExposure: rawCoachCareerExperienceCulturalExposure,
    }
  } = currentListing.attributes;

  const coachCareerExperienceRoles = asRowsPadded(rawCoachCareerExperienceRoles, EMPTY_ROLE_ROW, 3);
  const coachCareerExperienceCulturalExposure = asRows(rawCoachCareerExperienceCulturalExposure, EMPTY_CULTURAL_EXPOSURE_ROW, 10);

  const isPublished = currentListing.id && currentListing.attributes.state !== LISTING_STATE_DRAFT;
  const panelTitle = isPublished ? (
    <FormattedMessage
      id="EditListingDescriptionPanel.title"
      values={{
        listingTitle: (
          <ListingLink listing={listing}>
            <FormattedMessage id="EditListingDescriptionPanel.listingTitle" />
          </ListingLink>
        ),
      }}
    />
  ) : (
    <FormattedMessage id="EditListingDescriptionPanel.createListingTitle" />
  );

  return (
    <div className={classes}>
      <h1 className={css.title}>{panelTitle}</h1>
      <EditListingDescriptionForm
        className={css.form}
        initialValues={{
          description,
          introductionVideo,
          coachingExperience,
          previousClients,
          coachingFormat,
          coachingExperienceByCareerPhase: Array.isArray(coachingExperienceByCareerPhase)
            ? coachingExperienceByCareerPhase
            : [],
          previousCoacheeTitles:
            typeof previousCoacheeTitles === 'string' ? previousCoacheeTitles : '',
          coachCareerExperienceRoles,
          coachCareerExperienceCulturalExposure,
        }}
        saveActionMsg={submitButtonText}
        onSubmit={values => {
          const {
            description,
            introductionVideo,
            coachingExperience,
            previousClients,
            coachingFormat,
            coachingExperienceByCareerPhase,
            previousCoacheeTitles,
            coachCareerExperienceRoles,
            coachCareerExperienceCulturalExposure,
          } = values;
          const updateValues = {
            description,
            publicData: {
              introductionVideo,
              coachingExperience,
              previousClients: previousClients?.trim(),
              coachingFormat,
              coachingExperienceByCareerPhase: Array.isArray(coachingExperienceByCareerPhase)
                ? coachingExperienceByCareerPhase
                : [],
              previousCoacheeTitles:
                typeof previousCoacheeTitles === 'string' ? previousCoacheeTitles.trim() : '',
              coachCareerExperienceRoles: normalizeRoleRows(coachCareerExperienceRoles),
              coachCareerExperienceCulturalExposure: normalizeCulturalExposureRows(
                coachCareerExperienceCulturalExposure
              ),
            },
          };

          onSubmit(updateValues);
        }}
        onChange={onChange}
        disabled={disabled}
        ready={ready}
        updated={panelUpdated}
        updateInProgress={updateInProgress}
        fetchErrors={errors}
      />
    </div>
  );
};

EditListingDescriptionPanel.defaultProps = {
  className: null,
  rootClassName: null,
  errors: null,
  listing: null,
};

EditListingDescriptionPanel.propTypes = {
  className: string,
  rootClassName: string,

  // We cannot use propTypes.listing since the listing might be a draft.
  listing: object,

  disabled: bool.isRequired,
  ready: bool.isRequired,
  onSubmit: func.isRequired,
  onChange: func.isRequired,
  submitButtonText: string.isRequired,
  panelUpdated: bool.isRequired,
  updateInProgress: bool.isRequired,
  errors: object.isRequired,
};

export default EditListingDescriptionPanel;
